import {BaseFeatureFields, useFeaturesQuery} from './useFeaturesQuery';
import {useEffect, useState} from 'react';
import {GetFeaturesResponse} from '../../services/api-types';
import {FeatureToggleName, UseBaseFeature, useBaseFeature} from './useBaseFeature';

export interface UseTollProviderOutageCheck extends UseBaseFeature{
    readonly data?: {
        /** Whether eToll outage messaging should be shown to the user */
        readonly displayEtollOutage: boolean;
        /** Whether Linkt outage messaging should be shown to the user */
        readonly displayLinktOutage: boolean;
        /** List of paragraphs of copy to be displayed when etoll is having an outage */
        readonly etollMessaging?: readonly string[];
        /** List of paragraphs of copy to be displayed when linkt is having an outage */
        readonly linktMessaging?: readonly string[];
    };
}

/**
 * Hook for managing a specific feature for toll provider outages.
 * It checks if toll providers having outage and provides a list of messages to be displayed to the user.
 * @returns Object containing methods to get current outages and loading state, which depends on the useFeaturesQuery hook.
 */
export function useTollProviderOutageCheck(): UseTollProviderOutageCheck {
    const featureToggle = useBaseFeature();

    const {isLoadingError, error, isFeatureQueryLoading, featureResponse} = featureToggle;
    const [result, setResult] = useState<UseTollProviderOutageCheck | null>(null);

    if (isLoadingError) {
        console.error('Failed during fetching of feature query', error);
    }

    useEffect(() => {
        const handleInterval = (isLoading: boolean, data?: GetFeaturesResponse) => {
            setResult(
                calculateOutages(isLoading, data)
            );
        }

        // Check once at the start
        handleInterval(isFeatureQueryLoading, featureResponse);

        // Set up a continual check
        const interval = setInterval(handleInterval, ONE_MINUTE_IN_MILLIS, isFeatureQueryLoading, featureResponse);

        // Cleanup the interval when react deps change or component destruction
        return () => clearInterval(interval);
    }, [featureResponse, isFeatureQueryLoading]);

    const calculateOutages = (isFeatureQueryLoading: boolean, data?: GetFeaturesResponse): UseTollProviderOutageCheck => {
        if (isFeatureQueryLoading) {
            return {
                isFeatureQueryLoading: true,
            } as UseTollProviderOutageCheck;
        }

        const etollOutageFeature = data?.features.find(
            ({name}) => name === FeatureToggleName.TOLL_PROVIDER_OUTAGE_ETOLL)
            ?.fields as ProviderOutage | undefined;

        const linktOutageFeature = data?.features.find(
            ({name}) => name === FeatureToggleName.TOLL_PROVIDER_OUTAGE_LINKT)
            ?.fields as ProviderOutage | undefined;

        const displayEtollOutage = etollOutageFeature !== undefined &&
            shouldProviderOutageBeDisplayed(etollOutageFeature);
        const displayLinktOutage = linktOutageFeature !== undefined &&
            shouldProviderOutageBeDisplayed(linktOutageFeature);

        return {
            data: {
                displayEtollOutage: displayEtollOutage,
                displayLinktOutage: displayLinktOutage,
                etollMessaging: displayEtollOutage ? etollOutageFeature.messages : undefined,
                linktMessaging: displayLinktOutage ? linktOutageFeature.messages : undefined,
            },
            ...featureToggle
        };
    }

    return result ?? {isFeatureQueryLoading: true} as UseTollProviderOutageCheck;
}

const ONE_MINUTE_IN_MILLIS = 60_000;

interface ProviderOutage extends BaseFeatureFields {
    /**
     * The copy to be displayed to the user when the outage banner is up.
     * Each list item corresponds to a paragraph.
     * @example ["Linkt is down! Try again later.", "If the problem persists, contact Ferry."]
     */
    readonly messages: readonly string[];
    /**
     * Time of when we want to allow the outage messaging to display.
     * Only really for planned outages.
     * Use `startDateTime` for the actual outage start time.
     * @example "2023-04-15T14:30:00Z"
     */
    readonly startDisplayDateTime?: string;
}

function shouldProviderOutageBeDisplayed(outage: ProviderOutage): boolean {
    // Minimum between start of outage, or start of display time
    const startTime = outage.startDisplayDateTime !== undefined ?
        new Date(
            Math.min(
                new Date(outage.startDateTime).getTime(),
                new Date(outage.startDisplayDateTime).getTime(),
            ),
        ) :
        new Date(outage.startDateTime);
    const endTime = new Date(outage.endDateTime ?? '2099-01-01');

    const now = new Date();
    return startTime < now && now < endTime;
}
